import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, IconButton, TextField } from '@mui/material';
import { Close as CloseIcon, ThumbUpAltOutlined as UpIcon, ThumbDownAltOutlined as DownIcon, StarRounded as StarIcon } from '@mui/icons-material';
import { DIALOG_STYLES } from '../styles/dialogStyles';
import { StandardButton, Txt } from './design-system';
import { fetchReviewEligibility, openWriteReview, recordReviewEvent, requestNativeRating, reviewStoreName, isIosApp, isAndroidApp } from '../utils/appReview';

/**
 * The store-review ask.
 *
 * Shows once, right after the user logs something or opens real bloodwork
 * results (win moments), only when the server says the account has earned it: 3+ days old, food logged on 3+
 * days, asked fewer than twice ever, 30+ days since the last ask, never
 * answered. It never appears on open, never in the same session as the
 * notification ask, and a thumbs up or down ends the asking for good.
 *
 * Thumbs up → the native apps show the store's own star sheet (Apple's on
 * iOS, Google Play's in-app review on Android), then one soft "add a few
 * words" with a direct link to the review form; the web goes straight to it.
 *
 * This is the only rating path in the app: every ask is server-gated and
 * tracked, and at most one is shown per session. Thumbs down → a private "what would
 * make it better" box that goes to us, not the store.
 */
const SHOWN_THIS_SESSION = 'ft_review_prompt_shown';
const NOTIF_SHOWN_THIS_SESSION = 'ft_notif_prompt_shown';
const NEXT_CHECK_KEY = 'ft_review_next_check';
const LOCAL_BACKOFF_DAYS = 1;

type Step = 'ask' | 'write' | 'feedback' | 'thanks';

const mono = 'var(--font-mono)';

export const ReviewPrompt: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [step, setStep] = useState<Step>('ask');
  const [feedback, setFeedback] = useState('');
  const [sending, setSending] = useState(false);
  const storeName = reviewStoreName();

  const dismiss = () => {
    if (step === 'ask') recordReviewEvent('dismissed');
    onClose();
  };

  const thumbsUp = async () => {
    recordReviewEvent('up');
    if (isIosApp() || isAndroidApp()) {
      // The store's own sheet first; the written ask waits a beat so the two never overlap.
      await requestNativeRating();
      setTimeout(() => setStep('write'), 1200);
      return;
    }
    setStep('write');
  };

  const thumbsDown = () => {
    recordReviewEvent('down');
    setStep('feedback');
  };

  // Already reviewed in the past → count it and never ask again.
  const alreadyDid = () => {
    recordReviewEvent('up');
    recordReviewEvent('wrote');
    onClose();
  };

  const write = () => {
    recordReviewEvent('wrote');
    openWriteReview();
    onClose();
  };

  const sendFeedback = async () => {
    setSending(true);
    await recordReviewEvent('down', feedback.trim());
    setSending(false);
    setStep('thanks');
    setTimeout(onClose, 1800);
  };

  const title = step === 'ask' ? 'Is FuelTron helping?' : step === 'write' ? 'Thank you' : step === 'feedback' ? 'Sorry to hear that' : 'Got it';

  return (
    <Dialog open onClose={dismiss} {...DIALOG_STYLES.standard}>
      <DialogTitle sx={DIALOG_STYLES.title}>
        <Box className="icon-text-row" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon sx={{ fontSize: 20, color: '#FFD740', filter: 'drop-shadow(0 0 6px #FFD74099)' }} />
          <Typography component="span">{title}</Typography>
        </Box>
        <IconButton onClick={dismiss} size="small" sx={DIALOG_STYLES.closeButton} aria-label="Not now">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={DIALOG_STYLES.content}>
        {step === 'ask' && (
          <>
            <Txt sx={{ fontSize: '0.9rem', lineHeight: 1.5, color: 'rgba(255,255,255,0.85)' }}>
              You have been logging for a few days now. One honest answer helps us decide what to build next.
            </Txt>
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <StandardButton variant="outlined" onClick={thumbsDown} sx={{ ...DIALOG_STYLES.secondaryButton, flex: 1, gap: 1 }}>
                <DownIcon sx={{ fontSize: 18 }} /> Not really
              </StandardButton>
              <StandardButton variant="primary" onClick={thumbsUp} sx={{ flex: 1, gap: 1 }}>
                <UpIcon sx={{ fontSize: 18 }} /> Yes, it is
              </StandardButton>
            </Box>
            <Txt onClick={alreadyDid} sx={{ mt: 1.5, textAlign: 'center', fontFamily: mono, fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', cursor: 'pointer', '&:hover': { color: '#00E0FF' } }}>
              I already left a review
            </Txt>
          </>
        )}

        {step === 'write' && (
          <>
            <Txt sx={{ fontSize: '0.9rem', lineHeight: 1.5, color: 'rgba(255,255,255,0.85)' }}>
              Would you add a few words on {storeName}? A sentence from a real user does more for a small app than anything we could write.
            </Txt>
            <Txt sx={{ fontSize: '0.8rem', lineHeight: 1.45, color: 'rgba(255,255,255,0.6)', mt: 1 }}>
              A starting point: what do you use most? Photo scans, bloodwork, Garmin sync, supplements, the coach?
            </Txt>
          </>
        )}

        {step === 'feedback' && (
          <>
            <Txt sx={{ fontSize: '0.9rem', lineHeight: 1.5, color: 'rgba(255,255,255,0.85)' }}>
              What would make it better? This goes straight to the people building FuelTron, not to a store.
            </Txt>
            <TextField
              label="Your note"
              placeholder="Anything at all — a missing feature, something confusing, something that broke"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value.slice(0, 2000))}
              multiline
              minRows={3}
              fullWidth
              autoFocus
              sx={{ mt: 1.5 }}
            />
          </>
        )}

        {step === 'thanks' && (
          <Txt sx={{ fontSize: '0.9rem', lineHeight: 1.5, color: 'rgba(255,255,255,0.85)' }}>
            Thank you. We read every one of these.
          </Txt>
        )}
      </DialogContent>

      {step === 'write' && (
        <DialogActions sx={DIALOG_STYLES.actions}>
          <StandardButton variant="outlined" onClick={onClose} sx={DIALOG_STYLES.secondaryButton}>Maybe later</StandardButton>
          <StandardButton variant="primary" onClick={write} sx={DIALOG_STYLES.primaryButton}>Write a review</StandardButton>
        </DialogActions>
      )}
      {step === 'feedback' && (
        <DialogActions sx={DIALOG_STYLES.actions}>
          <StandardButton variant="outlined" onClick={onClose} sx={DIALOG_STYLES.secondaryButton}>Skip</StandardButton>
          <StandardButton variant="primary" onClick={sendFeedback} disabled={sending || !feedback.trim()} sx={DIALOG_STYLES.primaryButton}>
            {sending ? 'Sending…' : 'Send'}
          </StandardButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

/**
 * Decides when to show it. Listens for the same "logged" events as the
 * notification ask, plus 'bloodworkViewed' (real lab results on screen), waits a few seconds so that ask wins if both qualify,
 * asks the server whether this account has earned it, and keeps a one-day
 * local back-off so the check itself never becomes chatter.
 */
export const useReviewPrompt = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onLogged = () => {
      try {
        if (sessionStorage.getItem(SHOWN_THIS_SESSION) === '1') return;
        if (!localStorage.getItem('token')) return;
        const next = parseInt(localStorage.getItem(NEXT_CHECK_KEY) || '0', 10);
        if (next && Date.now() < next) return;
        sessionStorage.setItem(SHOWN_THIS_SESSION, '1');
        timer = setTimeout(async () => {
          // The notification ask took this moment — never stack two asks.
          if (sessionStorage.getItem(NOTIF_SHOWN_THIS_SESSION) === '1') return;
          const r = await fetchReviewEligibility();
          if (!r) return;
          if (!r.eligible) {
            const days = r.reason === 'answered' || r.reason === 'max_prompts' || r.reason === 'internal' ? 365 : r.reason === 'recent' ? 30 : LOCAL_BACKOFF_DAYS;
            localStorage.setItem(NEXT_CHECK_KEY, String(Date.now() + days * 864e5));
            return;
          }
          localStorage.setItem(NEXT_CHECK_KEY, String(Date.now() + 30 * 864e5));
          recordReviewEvent('shown');
          setShow(true);
        }, 4000);
      } catch { /* never break logging */ }
    };
    window.addEventListener('foodLogged', onLogged);
    window.addEventListener('exerciseLogged', onLogged);
    window.addEventListener('bloodworkViewed', onLogged);
    return () => {
      window.removeEventListener('foodLogged', onLogged);
      window.removeEventListener('exerciseLogged', onLogged);
      window.removeEventListener('bloodworkViewed', onLogged);
      if (timer) clearTimeout(timer);
    };
  }, []);

  return { showReviewPrompt: show, setShowReviewPrompt: setShow };
};

export default ReviewPrompt;
